import { DeleteOutlined, DragOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { Form, Button, Divider, Empty, Modal, Tooltip, Radio } from "antd"
import { Field, FieldArray, destroy, getFormValues, initialize, change } from "redux-form"
import { FormattedMessage, injectIntl } from "react-intl"
import React, { Component, Fragment } from "react"
import { isEmpty, sortBy, uniqueId } from "lodash"
import PropTypes from "prop-types"
import { Twemoji } from "react-emoji-render"
import { connect } from "react-redux"
import flag from "country-code-emoji"
import titleize from "titleize"

import {
  FileDragAndDropField,
  IconPickerField,
  InputField,
  SwitchField,
} from "src/inputs/connected-input-fields"
import FormRadioGroup from "src/inputs/form-fields/FormRadioGroup"

import { ACTIONS, OPF_ITEM_TYPES } from "../../globals"
import { bindActions, bindSelectors } from "src/store/utils"
import {
  getNodeIdByItemSlug,
  linkToFile,
  normalizeLanguageCode,
  sanitizeHTML,
} from "src/components/helpers"
import ClassificationSelect from "src/inputs/ClassificationSelect"
import FormattedTitle from "src/components/FormattedTitle"
import ProcedureStep from "src/components/opf-items/ProcedureStep"
import actions from "src/store/actions"
import { required } from "src/forms/validators"
import selectors from "src/store/selectors"
import ItemMeta from "src/components/ItemMeta"
import Placeholder from "src/inputs/Placeholder"

// FIXME: This component need styling from Procedure to look right.
import "src/components/opf-items/Procedure"
import "./style.less"
import LexicalEditor from "../../inputs/rich-text/LexicalEditor"
import { TEMP_STEP_PREFIX } from "../../components/opf-items/ProcedureSplitView/constants"
import { isPdf, getIcon } from "../../components/helpers"
import classNames from "classnames"

const form = "procedureForm"

export class ProcedureEditForm extends Component {
  state = {
    loading: false,
    savingDraft: false,
    publishing: false,
    previewVisible: false,
    previewImage: "",
    isReviewDateDisabled: false,
    formUpdaterCounter: 0,
  }

  handleInitialize(item) {
    const { initializeForm, procedureEditMode } = this.props

    if (!item) return
    const {
      title,
      text,
      icon,
      mediaLibrary,
      nextReviewDate,
      nextReviewDateIsNotApplicable,
      viewableByExternals,
      steps,
      accessRoles,
      slug,
      classification,
      editorialVersion,
      documentOwner,
      documentNumber,
      informationContact,
      globalType,
    } =
      item.draftVersion !== null && procedureEditMode !== ACTIONS.TRANSLATE
        ? item.draftVersion
        : item
    const mediaFileList = mediaLibrary ? mediaLibrary.map(linkToFile).filter(Boolean) : []

    const newFormValues = {
      title: title,
      text: text || "",
      icon: icon,
      draft: null,
      mediaLibrary: mediaLibrary || [],
      procedureMediaFiles: mediaFileList ? { fileList: mediaFileList } : [],
      nextReviewDate: nextReviewDate,
      nextReviewDateIsApplicable: !nextReviewDateIsNotApplicable,
      viewableByExternals: viewableByExternals,
      accessRoles: accessRoles || [],
      classification: classification && classification !== 0 ? classification : 0,
      steps: steps ? steps.map((step) => ({ ...step, parentSlug: slug, archiveStep: false })) : [],
      editorialVersion: editorialVersion ?? "1.0",
      globalType: globalType ? globalType : "",
      documentOwner: documentOwner ?? "",
      documentNumber: documentNumber ?? "",
      informationContact: informationContact ?? "",
    }

    initializeForm(form, newFormValues)
  }

  renderSteps = ({ fields }) => {
    const { item, formValues, translating = false, showActions = true } = this.props
    const emptyDisplay =
      formValues && isEmpty(formValues.steps) ? (
        <Placeholder className="placeholder--medium">
          <Empty description={<FormattedMessage id="message.noSteps" />} />
        </Placeholder>
      ) : null
    const newObj = {
      stepId: uniqueId(TEMP_STEP_PREFIX),
      title: "",
      text: "",
      links: [],
      mediaLibrary: [],
      type: OPF_ITEM_TYPES.PROCEDURE_ELEMENT,
      parentSlug: item.slug || "",
      parentId: item.id || "",
      archiveStep: false,
      new: true,
    }

    return (
      <>
        {emptyDisplay}
        {fields &&
          fields.map((step, index) => (
            <ProcedureStep
              editMode
              index={index}
              item={fields.get(index)}
              key={fields.get(index).stepId}
              language={item.language}
              majorVersion={item.majorVersion}
              parentLanguage={item.language}
              previewFile={this.handlePreview}
              showActions={showActions}
              translating={translating}
            />
          ))}
        {showActions && !translating && (
          <Button className="step__add-btn" onClick={() => fields.push(newObj)}>
            <FormattedTitle id="button.addStep" />
          </Button>
        )}
      </>
    )
  }

  handlePreview = (file) => {
    if (isPdf(file)) {
      return window.open(file.url, "_blank", "noopener noreferrer")
    }
    this.setState({
      previewImage: file.thumbUrl ? file.thumbUrl : file.url,
      previewVisible: true,
    })
  }

  handleCancelPreview = () => this.setState({ previewVisible: false })

  customUploadRequest = ({ onSuccess, file }) => {
    const { uploadFile, getItemBySlug } = this.props
    const item = this.props.item.opfId ? this.props.item : getItemBySlug(this.props.item.parentSlug)
    return uploadFile({ opfItem: item, file }).then((result) => onSuccess(result, result.media))
  }

  removeFile = (currentFile) => {
    const { changeForm, formValues } = this.props
    const fileList = formValues.procedureMediaFiles.fileList.filter(
      (file) => file.uid !== currentFile.uid
    )
    changeForm(form, "procedureMediaFiles.fileList", fileList)
  }

  handleSetStepOrderModal = () => {
    const { openFormModal, item, formValues } = this.props
    const newOrder = formValues.newOrder ? formValues.newOrder : null
    const edittedItem = newOrder
      ? { ...item, ...formValues, steps: newOrder }
      : { ...item, ...formValues }
    const currentItem = edittedItem.draftVersion !== null ? edittedItem.draftVersion : edittedItem
    openFormModal({ opfItem: currentItem, mode: ACTIONS.UPDATE_ORDER })
  }

  getServiceLineTreeNodes = (data) => {
    if (isEmpty(data)) return null
    const sortedData = sortBy(data, "name")
    return sortedData.map((item) => {
      const children = item && item.roles ? item.roles : null
      return {
        title: item.name,
        key: item.code,
        value: item.code,
        children: children ? this.getServiceLineTreeNodes(children) : null,
      }
    })
  }

  componentDidMount() {
    const { fetchServiceLines, item } = this.props

    fetchServiceLines({ language: item.language, fallbackLanguage: null })
    this.handleInitialize(item)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.currentLanguage !== this.props.currentLanguage) {
      const { procedureEditMode, setProcedureEditMode } = this.props
      if (procedureEditMode) setProcedureEditMode(false)
    }

    if (prevProps.item.id !== this.props.item.id) {
      this.props.destroyForm(form)
      this.handleInitialize(this.props.item)
    }
    // // auto translation fix to update fields
    if (
      prevProps.autoTranslatedPartialItem?.title !== this.props.autoTranslatedPartialItem?.title
    ) {
      this.setState((state) => ({ formUpdaterCounter: state.formUpdaterCounter + 1 }))
    }
  }

  render() {
    const {
      item,
      procedureEditMode,
      addProcedureMode = false,
      editProcedureMode = false,
      currentLanguage,
      defaultLanguage,
      mapToMediaLinks,
      splitView,
      parentItemTitle,
      formValues,
      serviceLines,
      intl,
      translating = false,
      getFormItemViewTitle,
      getFormItemViewDescription,
    } = this.props

    if (!item) return null
    const { slug } = item
    const editing = procedureEditMode === ACTIONS.EDIT
    const editingTranslatedItem = editing && item.translated
    const fileList =
      formValues &&
      formValues.procedureMediaFiles &&
      !isEmpty(formValues.procedureMediaFiles.fileList)
        ? mapToMediaLinks(formValues.procedureMediaFiles.fileList)
        : []
    const treeData = this.getServiceLineTreeNodes(serviceLines)
    if (!treeData) return null //Return early to prevent unnescessary renders

    const viewTitle = getFormItemViewTitle({ intl, item, addProcedureMode, parentItemTitle })
    const viewDescription = getFormItemViewDescription({ intl, item, addProcedureMode })
    const isTranslating =
      editing && !editingTranslatedItem && currentLanguage !== defaultLanguage
        ? item.language !== defaultLanguage && translating
        : translating

    return (
      <Fragment
        key={"form_" + getNodeIdByItemSlug(slug) + (translating ? "translating" : "editing")}
      >
        <Form>
          <div
            className="page procedure procedure--edit fade-in"
            id={getNodeIdByItemSlug(slug)}
            style={this.props.style}
          >
            {(procedureEditMode === ACTIONS.EDIT && item.global && !splitView) ||
            addProcedureMode ||
            editProcedureMode ? (
              <div className="edit__header edit__header--singleColumn">
                <h1>
                  <FormattedMessage {...viewTitle} />
                </h1>
                <p
                  className="edit__description"
                  dangerouslySetInnerHTML={sanitizeHTML(viewDescription)}
                />
                <div className="edit__flag-indicator edit__flag-indicator--editMode">
                  <Twemoji
                    svg
                    text={flag(
                      normalizeLanguageCode(
                        item.language !== currentLanguage || (addProcedureMode && item.global)
                          ? defaultLanguage
                          : currentLanguage
                      )
                    )}
                  />
                </div>
              </div>
            ) : null}
            <div className="row">
              <div>
                <Field
                  name="icon"
                  component={IconPickerField}
                  disabled={isTranslating}
                  label={titleize(intl.formatMessage({ id: "common.icon" }))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Field
                  key={"title_" + this.state.formUpdaterCounter}
                  name="title"
                  itemKey={
                    "edit-form-title" + item.id + "_" + item.language + "_" + item.majorVersion
                  }
                  component={InputField}
                  label={titleize(intl.formatMessage({ id: "common.title" }))}
                  placeholder={titleize(`${intl.formatMessage({ id: "common.title" })}...`)}
                  validate={[required]}
                  required
                />
              </div>
            </div>
            <div className="ant-form-item-label">
              <label className="text-label">
                <label>{titleize(intl.formatMessage({ id: "common.description" }))}</label>
              </label>
            </div>
            <div className="description">
              <Field
                name="text"
                component={LexicalEditor}
                itemKey={"procedure_" + item.id + "_" + item.language + "_" + item.majorVersion}
                className="LexicalEditor--white rich-text-editor"
                placeholder={titleize(`${intl.formatMessage({ id: "common.description" })}...`)}
                preserveFormOnUnmount
              />
            </div>
            <>
              {isEmpty(fileList) && translating ? null : (
                <label className="text-label">
                  <FormattedTitle
                    id="common.pdf"
                    values={{ one: () => "", plural: (chunk) => chunk }}
                  />{" "}
                  :
                </label>
              )}
              <div className="media-wall">
                {isEmpty(fileList)
                  ? null
                  : fileList.map((file, index) => (
                      <div className="media" key={index}>
                        <div className="media__image-wrap">
                          <Tooltip
                            title={
                              <FormattedTitle
                                id="common.remove"
                                values={{
                                  target: () =>
                                    intl.formatMessage(
                                      { id: isPdf(file) ? "common.pdf" : "common.image" },
                                      { one: (chunk) => chunk, plural: () => "" }
                                    ),
                                }}
                              />
                            }
                          >
                            <Button
                              className="media__delete"
                              size="small"
                              icon={<DeleteOutlined />}
                              shape="circle"
                              onClick={() => this.removeFile(file)}
                            />
                          </Tooltip>
                          <div
                            className={classNames("media__image", isPdf(file) && "media__pdf")}
                            onClick={() => this.handlePreview(file)}
                            style={
                              isPdf(file)
                                ? {}
                                : {
                                    backgroundImage: `url(${file.thumbUrl ? file.thumbUrl : file.url})`,
                                  }
                            }
                          >
                            {isPdf(file) && getIcon(file.url, { isTwoTone: true })}
                          </div>
                        </div>
                      </div>
                    ))}
                <Tooltip tile="Recommended aspect ratio 16:9" mouseEnterDelay={0.5}>
                  <div>
                    <Field
                      name="procedureMediaFiles"
                      className="media-wall__upload-field"
                      component={FileDragAndDropField}
                      customUploadRequest={this.customUploadRequest}
                      acceptedFileType="image/png,image/jpg,image/jpeg,image/gif,application/pdf"
                      multiple={false}
                      item={item}
                      defaultValue={isEmpty(fileList) ? {} : { fileList, file: {} }}
                    />
                  </div>
                </Tooltip>
              </div>
            </>
            <ItemMeta>
              {/* <div className="item-meta__item col-xs-12">
                <div className="item-meta__value tree-select-dropdown">
                  <Field
                    name="accessRoles"
                    component={TreeSelectField}
                    value={this.state.selectedServiceLines}
                    defaultValue={item ? item.accessRoles : null}
                    onBlur={(event) => event.preventDefault()}
                    treeCheckable
                    showSearch
                    disabled={isTranslating}
                    loading={loading}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder={intl.formatMessage({
                      id: "common.selectServicelinePlaceholder",
                    })}
                    label={intl.formatMessage({ id: "common.relevantFor" })}
                    dropdownStyle={{ maxHeight: 310, overflowY: "scroll" }}
                    treeData={treeData}
                  />*/}
              <div className="item-meta__item col-xs-12 col-sm-6 col-md-4">
                <div className="item-meta__value">
                  <ClassificationSelect disabled={isTranslating} required />
                </div>
              </div>
              <div className="item-meta__item col-xs-12">
                <label className="text-label">
                  <FormattedMessage id="common.uniqueNumber" />:
                </label>
                <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                  <div>
                    <Field
                      name="generateUniqueNumber"
                      component={SwitchField}
                      disabled={translating}
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                    />
                  </div>
                  <div className="col-xs-8">
                    <Tooltip
                      title={
                        !formValues?.generateUniqueNumber
                          ? intl.formatMessage({ id: "common.uniqueNumber" })
                          : intl.formatMessage({ id: "common.uniqueNumberNotDuplicate" })
                      }
                    >
                      <div>
                        <Field
                          name="documentNumber"
                          key="documentNumber"
                          component={InputField}
                          defaultValue={item.documentNumber}
                          disabled={!formValues?.generateUniqueNumber ? true : translating}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
              <div className="item-meta__item col-xs-12 col-sm-6 col-md-4">
                <div className="ant-form-item-label">
                  <label className="ant-form-item-required text-label">
                    {intl.formatMessage({ id: "common.documentOwnerEmailAddress" })}
                  </label>
                </div>
                <Field
                  name="documentOwner"
                  component={InputField}
                  disabled={isTranslating}
                  defaultValue={null}
                  itemKey="edit-form-documentOwner"
                  validate={[required]}
                  required
                />
              </div>
              <div className="item-meta__item col-xs-12 col-sm-6 col-md-4">
                <label className="text-label">
                  {intl.formatMessage({ id: "common.informationContactEmailAddress" })} :
                </label>
                <Field
                  name="informationContact"
                  component={InputField}
                  disabled={isTranslating}
                  defaultValue={null}
                  itemKey="edit-form-informationContact"
                />
              </div>
              <div className="item-meta__item col-xs-12">
                <label className="text-label">
                  {intl.formatMessage({ id: "common.globalType" })}:
                </label>
                <div style={{ marginTop: 14 }}>
                  <Field
                    name="globalType"
                    component={FormRadioGroup}
                    // validate={[required]}
                    className="radio-group"
                    disabled={isTranslating}
                    defaultValue={item.globalType}
                    itemKey="edit-form-globalType"
                  >
                    <Radio value="global">Global</Radio>
                    <Radio value="group">Group</Radio>
                  </Field>
                </div>
              </div>
            </ItemMeta>
            <div className="col-xs-12 steps">
              <div className="row middle-xs">
                <h2 style={{ paddingRight: 15, lineHeight: "40px" }}>
                  <FormattedTitle
                    id="common.step"
                    values={{ one: () => "", plural: (chunk) => chunk }}
                  />
                </h2>
                {formValues && !isEmpty(formValues.steps) && !translating ? (
                  <Button onClick={this.handleSetStepOrderModal}>
                    <DragOutlined />
                    <span>
                      <FormattedMessage id="button.setOrder" />
                    </span>
                  </Button>
                ) : null}
                <Divider style={{ margin: "15px 0 0 0" }} />
              </div>
              <FieldArray
                name="steps"
                component={this.renderSteps}
                props={{ item: item }}
                withRef
              />
            </div>
          </div>
        </Form>
        <Modal
          className="media-wall-preview-modal"
          open={this.state.previewVisible}
          footer={null}
          onCancel={this.handleCancelPreview}
          centered
          style={{ position: "relative", maxHeight: "96vh", textAlign: "center" }}
        >
          <img alt="example" src={this.state.previewImage} />
        </Modal>
      </Fragment>
    )
  }
}

ProcedureEditForm.propTypes = {
  openFormModal: PropTypes.func,
  uploadFile: PropTypes.func,
  destroyForm: PropTypes.func,
  update: PropTypes.func,
  // createVariant: PropTypes.func,
  mapToMediaLinks: PropTypes.func,
  getItemBySlug: PropTypes.func,
  procedureEditMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}

// const ProcedureFormComponent = reduxForm({ form })(ProcedureEditForm)
const mapStateToProps = bindSelectors({
  currentLanguage: selectors.localeSelectors.getCurrentLanguage,
  defaultLanguage: selectors.localeSelectors.getDefaultLanguage,
  formValues: getFormValues(form),
  mapToMediaLinks: selectors.mapToMediaLinks,
  procedureEditMode: selectors.itemSelectors.getProcedureEditModeState,
  serviceLines: selectors.itemSelectors.getServiceLines,
  getItemBySlug: selectors.itemSelectors.getItemBySlug,
  getFormItemViewTitle: selectors.itemSelectors.getFormItemViewTitle,
  getFormItemViewDescription: selectors.itemSelectors.getFormItemViewDescription,
  autoTranslatedPartialItem: selectors.itemSelectors.getAutoTranslatedPartialItem,
})
const mapDispatchToProps = bindActions({
  setProcedureEditMode: actions.itemActions.setProcedureEditMode,
  openFormModal: actions.uiActions.openFormModal,
  uploadFile: actions.itemActions.uploadFile,
  update: actions.itemActions.updateItem,
  fetchServiceLines: actions.itemActions.fetchServiceLines,
  resetAutoTranslatedItem: actions.itemActions.resetAutoTranslatedItem,
  destroyForm: destroy,
  initializeForm: initialize,
  changeForm: change,
})

const IntlProcedureForm = injectIntl(ProcedureEditForm)
export default connect(mapStateToProps, mapDispatchToProps)(IntlProcedureForm)

// where to find: https://localhost:44309/processes
// Role: Global Process Owner
// navigate to a document in Procedures / Work Instructions
// click: ... (menu) -> Edit
